/**
 * A beacon's color, as the API stores it (api/v4/beacons.md §3 in the Go repo): empty for the
 * default, or `color:<name>-<shade>` naming one of the app's palette tokens
 * (`--amplify-colors-<name>-<shade>`). The API checks the shape; which names render is decided
 * here: eight hues in three shades.
 */

export const BEACON_COLOR_NAMES = [
  'red',
  'orange',
  'yellow',
  'green',
  'teal',
  'blue',
  'purple',
  'pink',
] as const;

/** Light, mid and dark of each hue; the palette's 40, 60 and 80. */
export const BEACON_COLOR_SHADES = [40, 60, 80] as const;

/** The fill of a beacon without a color: the map's orange since the beacons began. */
export const DEFAULT_BEACON_FILL = '#ff7a45';

/** The stored form of a palette color. */
export const beaconColorToken = (name: string, shade: number): string => `color:${name}-${shade}`;

/** Every color the picker offers, shade by shade: a row of the hues per shade. */
export const BEACON_COLOR_ROWS: string[][] = BEACON_COLOR_SHADES.map((shade) =>
  BEACON_COLOR_NAMES.map((name) => beaconColorToken(name, shade)),
);

const TOKEN = /^color:([a-z]+)-([0-9]{1,3})$/;

/** The CSS variable a stored color names; null for none, or one outside the palette. */
export function beaconColorVar(color: string | undefined): string | null {
  const match = TOKEN.exec(color ?? '');
  if (!match) return null;
  const [, name, shade] = match;
  if (!(BEACON_COLOR_NAMES as readonly string[]).includes(name)) return null;
  if (!(BEACON_COLOR_SHADES as readonly number[]).includes(Number(shade))) return null;
  return `--amplify-colors-${name}-${shade}`;
}

/** The color as CSS: the variable, or the default fill. */
export function beaconColorCss(color: string | undefined): string {
  const variable = beaconColorVar(color);
  return variable ? `var(${variable})` : DEFAULT_BEACON_FILL;
}

/**
 * The color as Mapbox takes it: the variable's value as the page's theme sets it, read off the
 * themed container (the dark theme overrides the tokens there, not on the document), since the
 * map's paint cannot read CSS variables; the default fill without one.
 */
export function beaconFillColor(color: string | undefined): string {
  const variable = beaconColorVar(color);
  if (!variable || typeof document === 'undefined') return DEFAULT_BEACON_FILL;
  const root = document.querySelector('[data-amplify-theme]') ?? document.documentElement;
  const value = getComputedStyle(root).getPropertyValue(variable).trim();
  return value || DEFAULT_BEACON_FILL;
}
